import { useEffect, useState } from "react";
import {
  acceptInvite, clearAuthToken, completePasswordReset, confirmSignup, humanError,
  passwordProblem, readAuthToken, requestPasswordReset, signIn, signUp, siteSettings,
} from "../identity.js";
import { Button, ErrorPanel, Field, Fields, Input, Ribbon } from "../ui.jsx";
import { FlowStacked } from "../components/Logo.jsx";

/**
 * Every unauthenticated screen, in one place and on-brand.
 *
 * Which one shows is decided by the token in the URL: an invitation lands on
 * "set your password", a recovery link on "choose a new password", a confirmation
 * link confirms itself, and everything else gets the sign-in form.
 */
export default function Gate({ onSignedIn }) {
  const [mode, setMode] = useState(null); // null until the URL is read
  const [token, setToken] = useState(null);
  const [notice, setNotice] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const found = readAuthToken({ hash: window.location.hash, search: window.location.search });
    if (found?.kind === "error") {
      setNotice(found.message);
      setMode("signin");
      clearAuthToken();
    } else if (found) {
      setToken(found.token);
      setMode(found.kind);
    } else {
      setMode("signin");
    }
    siteSettings().then(setSettings);
  }, []);

  const done = (user) => {
    clearAuthToken();
    onSignedIn(user);
  };

  return (
    <div className="gate">
      <div className="box">
        <div className="lock">
          <FlowStacked size={112} tone="light" />
        </div>

        {mode === null && <div className="sm muted">Loading…</div>}

        {mode === "signin" && (
          <SignInForm notice={notice} settings={settings} onSignedIn={done} />
        )}
        {mode === "invite" && <AcceptInviteForm token={token} onSignedIn={done} />}
        {mode === "recovery" && <ResetPasswordForm token={token} onSignedIn={done} />}
        {(mode === "confirmation" || mode === "email_change") && (
          <ConfirmForm token={token} onSignedIn={done} />
        )}
      </div>
    </div>
  );
}

const Heading = ({ children, sub }) => (
  <>
    <div className="eyebrow">Invoicing</div>
    <h2 style={{ marginTop: 8 }}>{children}</h2>
    {sub && <p className="sm dim" style={{ marginTop: 6 }}>{sub}</p>}
  </>
);

const submitOnEnter = (fn) => (e) => {
  if (e.key === "Enter") fn();
};

/* ---------------------------------------------------------------- sign in */

function SignInForm({ notice, settings, onSignedIn }) {
  const [screen, setScreen] = useState("signin"); // signin | signup | forgot | sent
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(notice || null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const signupAllowed = settings ? !settings.disable_signup : false;

  async function submit() {
    setError(null);
    if (screen === "forgot") {
      if (!form.email.includes("@")) return setError("Enter the email address on your account.");
      setBusy(true);
      try {
        await requestPasswordReset(form.email);
        setScreen("sent");
      } catch (e) {
        setError(humanError(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (screen === "signup") {
      const problem = passwordProblem(form.password);
      if (problem) return setError(problem);
      setBusy(true);
      try {
        const res = await signUp(form.email, form.password, form.name);
        // Sites with autoconfirm on sign you straight in; otherwise Netlify emails a link.
        if (res?.confirmed_at || settings?.autoconfirm) {
          const user = await signIn(form.email, form.password);
          onSignedIn(user);
        } else {
          setScreen("sent");
        }
      } catch (e) {
        setError(humanError(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      onSignedIn(await signIn(form.email, form.password));
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  if (screen === "sent") {
    return (
      <>
        <Heading>Check your email</Heading>
        <Ribbon>We've sent a link to {form.email}. It's valid for 24 hours.</Ribbon>
        <p className="tiny muted" style={{ textAlign: "left" }}>
          Not arrived in a few minutes? Look in your junk folder — and if your mail client warns that
          links are disabled, mark the message as safe first, otherwise the link won't work.
        </p>
        <Button style={{ width: "100%", justifyContent: "center", marginTop: 12 }} onClick={() => setScreen("signin")}>
          Back to sign in
        </Button>
      </>
    );
  }

  const isForgot = screen === "forgot";
  const isSignup = screen === "signup";

  return (
    <>
      <Heading
        sub={
          isForgot
            ? "We'll email you a link to set a new one."
            : isSignup
            ? "Create an account for your business."
            : "Invoices, time and expenses for the team."
        }
      >
        {isForgot ? "Reset your password" : isSignup ? "Create an account" : "Sign in"}
      </Heading>

      <Fields style={{ marginTop: 18, textAlign: "left" }}>
        {error && <ErrorPanel error={{ message: error }} />}
        {isSignup && (
          <Field label="Your name">
            <Input value={form.name} onChange={set("name")} autoComplete="name" />
          </Field>
        )}
        <Field label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="username"
            onKeyDown={submitOnEnter(submit)}
          />
        </Field>
        {!isForgot && (
          <Field label="Password">
            <Input
              type="password"
              value={form.password}
              onChange={set("password")}
              autoComplete={isSignup ? "new-password" : "current-password"}
              onKeyDown={submitOnEnter(submit)}
            />
          </Field>
        )}
      </Fields>

      <Button
        kind="pri"
        busy={busy}
        style={{ width: "100%", justifyContent: "center", marginTop: 16 }}
        onClick={submit}
      >
        {isForgot ? "Email me a link" : isSignup ? "Create account" : "Sign in"}
      </Button>

      <div className="row" style={{ justifyContent: "center", gap: 14, marginTop: 14 }}>
        {!isForgot && (
          <span className="linky tiny" onClick={() => { setError(null); setScreen("forgot"); }}>
            Forgot password?
          </span>
        )}
        {isForgot && (
          <span className="linky tiny" onClick={() => { setError(null); setScreen("signin"); }}>
            Back to sign in
          </span>
        )}
        {signupAllowed && !isForgot && (
          <span
            className="linky tiny"
            onClick={() => { setError(null); setScreen(isSignup ? "signin" : "signup"); }}
          >
            {isSignup ? "I already have an account" : "Create an account"}
          </span>
        )}
      </div>

      {!isForgot && !isSignup && (
        <p className="tiny muted" style={{ marginTop: 16 }}>
          The first person to sign in becomes the account owner. After that, an owner invites everyone
          else from Netlify → Identity.
        </p>
      )}
    </>
  );
}

/* ------------------------------------------------------------ accept invite */

function AcceptInviteForm({ token, onSignedIn }) {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setError(null);
    const problem = passwordProblem(form.password, form.confirm);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      onSignedIn(await acceptInvite(token, form.password));
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Heading sub="Choose a password and you're in — nothing else to confirm.">
        Set your password
      </Heading>

      <Fields style={{ marginTop: 18, textAlign: "left" }}>
        {error && <ErrorPanel error={{ message: error }} />}
        <Field label="Password" hint="At least 8 characters.">
          <Input
            type="password"
            value={form.password}
            onChange={set("password")}
            autoComplete="new-password"
            onKeyDown={submitOnEnter(submit)}
          />
        </Field>
        <Field label="Confirm password">
          <Input
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            autoComplete="new-password"
            onKeyDown={submitOnEnter(submit)}
          />
        </Field>
      </Fields>

      <Button
        kind="pri"
        busy={busy}
        style={{ width: "100%", justifyContent: "center", marginTop: 16 }}
        onClick={submit}
      >
        Set password and continue
      </Button>
    </>
  );
}

/* ----------------------------------------------------------- reset password */

function ResetPasswordForm({ token, onSignedIn }) {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setError(null);
    const problem = passwordProblem(form.password, form.confirm);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      onSignedIn(await completePasswordReset(token, form.password));
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Heading sub="Pick something new and we'll sign you in.">Choose a new password</Heading>
      <Fields style={{ marginTop: 18, textAlign: "left" }}>
        {error && <ErrorPanel error={{ message: error }} />}
        <Field label="New password" hint="At least 8 characters.">
          <Input type="password" value={form.password} onChange={set("password")} autoComplete="new-password" onKeyDown={submitOnEnter(submit)} />
        </Field>
        <Field label="Confirm password">
          <Input type="password" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" onKeyDown={submitOnEnter(submit)} />
        </Field>
      </Fields>
      <Button kind="pri" busy={busy} style={{ width: "100%", justifyContent: "center", marginTop: 16 }} onClick={submit}>
        Save password and sign in
      </Button>
    </>
  );
}

/* ---------------------------------------------------------------- confirm */

function ConfirmForm({ token, onSignedIn }) {
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    confirmSignup(token)
      .then((user) => alive && onSignedIn(user))
      .catch((e) => alive && setError(humanError(e)));
    return () => {
      alive = false;
    };
  }, [token]);

  if (error) {
    return (
      <>
        <Heading>Couldn't confirm that link</Heading>
        <ErrorPanel error={{ message: error }} />
        <p className="tiny muted" style={{ textAlign: "left" }}>
          Confirmation links are single use, and some mail clients open them in the background before
          you click — which spends the link. Ask an owner to send a fresh invitation instead;
          invitations don't have that problem.
        </p>
        <Button
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
          onClick={() => {
            clearAuthToken();
            window.location.href = "/";
          }}
        >
          Back to sign in
        </Button>
      </>
    );
  }

  return (
    <>
      <Heading>Confirming your account…</Heading>
      <div style={{ display: "grid", placeItems: "center", padding: 18 }}>
        <span className="spinner" />
      </div>
    </>
  );
}
